import { escapeHtml } from './html.js';
import { t } from './strings.js';
/** Today's paper, exactly: every option at the registry default. */
export const DEFAULT_PRINT_OPTIONS = {
    headerText: '',
    footerText: '',
    showLogo: true,
    showItemCode: true,
    showDiscountColumn: true,
    showTaxColumn: true,
    showWarehouse: false,
    showBankDetails: false,
    bankDetails: '',
    paper: 'A4',
    language: 'follow_ui',
};
const PAPERS = ['A4', 'A5', 'thermal80'];
const LANGUAGES = ['follow_ui', 'en', 'ar'];
/**
 * The effective-settings map → options. The map is whatever the caller has:
 * the SPA's `/api/settings/effective` body, or the worker's per-key
 * `SettingsReader` reads folded into one object. Anything missing or of the
 * wrong shape falls back to its default rather than reaching the stylesheet.
 */
export function printOptionsFromSettings(settings) {
    const out = { ...DEFAULT_PRINT_OPTIONS };
    if (!settings)
        return out;
    const text = (key, fallback) => {
        const value = settings[key];
        return typeof value === 'string' ? value : fallback;
    };
    const flag = (key, fallback) => {
        const value = settings[key];
        return typeof value === 'boolean' ? value : fallback;
    };
    out.headerText = text('print.header_text', out.headerText);
    out.footerText = text('print.footer_text', out.footerText);
    out.showLogo = flag('print.show_logo', out.showLogo);
    out.showItemCode = flag('print.show_item_code', out.showItemCode);
    out.showDiscountColumn = flag('print.show_discount_column', out.showDiscountColumn);
    out.showTaxColumn = flag('print.show_tax_column', out.showTaxColumn);
    out.showWarehouse = flag('print.show_warehouse', out.showWarehouse);
    out.showBankDetails = flag('print.show_bank_details', out.showBankDetails);
    out.bankDetails = text('print.bank_details', out.bankDetails);
    const paper = settings['print.paper'];
    if (typeof paper === 'string' && PAPERS.includes(paper)) {
        out.paper = paper;
    }
    const language = settings['print.language'];
    if (typeof language === 'string' && LANGUAGES.includes(language)) {
        out.language = language;
    }
    return out;
}
/** `print.language` over the reader's own: `follow_ui` is today's behaviour. */
export function resolvePrintLocale(uiLocale, language) {
    return language === 'follow_ui' ? uiLocale : language;
}
/** Prose typed by an administrator: escaped, with its line breaks kept. */
function prose(text) {
    return escapeHtml(text.trim()).replaceAll(/\r?\n/g, '<br />');
}
/**
 * The rules an option adds to the shared stylesheet. Paper and letterhead are
 * CSS because they are about the PAGE, not the document's content; the three
 * trailing classes are always present and inert so a caller comparing output
 * at the defaults sees one stable block.
 */
function optionsCss(options) {
    const rules = [];
    if (options.paper === 'A5')
        rules.push('@page { size: A5; margin: 10mm 9mm 12mm; }');
    if (options.paper === 'thermal80') {
        rules.push('@page { size: 80mm auto; margin: 4mm; }', 'html { font-size: 10px; }', '.doc-header { flex-direction: column; }', '.doc-title { text-align: start; }', '.doc-fields { grid-template-columns: 1fr; }', '.doc-totals { inline-size: 100%; }', '.doc-cards { grid-template-columns: repeat(2, minmax(0, 1fr)); }');
    }
    if (!options.showLogo)
        rules.push('.doc-company { display: none; }');
    rules.push('.print-header-text { margin-block-end: 12px; color: #374151; }', '.print-footer-text { margin-block-start: 12px; color: #374151; }', '.print-bank-details { margin-block-start: 12px; border: 1px solid #e5e7eb; border-radius: 6px; padding: 8px 10px; break-inside: avoid; }');
    return rules.join('\n');
}
/**
 * The options that sit AROUND a document rather than in its table — paper,
 * letterhead, the header and footer prose, the bank block — applied to the
 * rendered page. Called by `renderDocumentHtml` for every kind; the invoice
 * table's own options (columns, the tax row, the warehouse) are the sales
 * invoice template's business, where a column can be left out rather than
 * hidden.
 *
 * Three seams opened by text search on markup the templates always emit: a
 * <style> before `</head>`, the header text after the first `</header>`, and
 * the bank block + footer text before the `<footer`. With every option at its
 * default the only addition is the inert style block.
 */
export function applyPrintOptions(html, options, locale) {
    let out = html.replace('</head>', `<style>\n${optionsCss(options)}\n</style>\n</head>`);
    if (options.headerText.trim() !== '') {
        const block = `<section class="print-header-text">${prose(options.headerText)}</section>`;
        out = out.includes('</header>')
            ? out.replace('</header>', `</header>\n${block}`)
            : out.replace('<body>', `<body>\n${block}`);
    }
    const tail = [];
    if (options.showBankDetails && options.bankDetails.trim() !== '') {
        tail.push(`<section class="print-bank-details"><strong>${escapeHtml(t('bankDetails', locale))}</strong><br />${prose(options.bankDetails)}</section>`);
    }
    if (options.footerText.trim() !== '') {
        tail.push(`<section class="print-footer-text">${prose(options.footerText)}</section>`);
    }
    if (tail.length > 0) {
        const joined = tail.join('\n');
        out = out.includes('<footer')
            ? out.replace('<footer', `${joined}\n<footer`)
            : out.replace('</body>', `${joined}\n</body>`);
    }
    return out;
}
//# sourceMappingURL=print-options.js.map